import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Form,
  Input,
  Label,
  FormFeedback,
  Row,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Spinner,
} from "reactstrap";
import OrganizationService from "../../../services/OrderingServices/OrganizationService";

const TenantForm = ({
  isOpen,
  toggle,
  selectedItem,
  fetchOrganizations,
  detailData,
}) => {
  console.log("🚀 ~ TenantForm ~ detailData:", detailData);
  const [error, setError] = useState(null);
  const [passwordShow, setPasswordShow] = useState(false);
  const [dbPasswordShow, setDbPasswordShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const id = detailData?.id;
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      organizationName: detailData?.organizationName || "",
      hostName: detailData?.tenantInfo?.hostName || "",
      server: detailData?.tenantInfo?.server || "",
      dbName: detailData?.tenantInfo?.dbName || "",
      password: "",
      dbUser: detailData?.tenantInfo?.dbUser || "",
      email: detailData?.email || "",
      tel: detailData?.tel || "",
      address1: detailData?.address1 || "",
      id: detailData?.id || "",
      dbPassword: detailData?.tenantInfo?.dbPassword || "",
      panNumber: detailData?.panNumber || "",
    },
    validationSchema: Yup.object({
      organizationName: Yup.string().required("Organization name is required"),
      hostName: Yup.string(),
      server: Yup.string().required("Server is required"),
      dbName: Yup.string().required("Database name is required"),

      password: Yup.string()
        .nullable()
        .matches(
          /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[@$!%*?&#]).{8,}$/,
          "Password must be at least 8 characters long, contain uppercase, lowercase, number, and special character"
        )
        .when("id", {
          is: (id) => !id, // if no id (create)
          then: (schema) => schema.required("Password is required"),
          otherwise: (schema) => schema.notRequired(), // optional on update
        }),

      dbPassword: Yup.string()
        .nullable()
        .matches(
          /[@$!%*?&#]/,
          "Database password must contain at least one special character"
        )
        .when("id", {
          is: (id) => !id,
          then: (schema) => schema.required("Database password is required"),
          otherwise: (schema) => schema.notRequired(),
        }),

      dbUser: Yup.string().required("Database user is required"),

      email: Yup.string()
        .required("Admin email is required")
        .matches(
          /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
          "Enter a valid email address"
        ),

      tel: Yup.string()
        .required("Telephone number is required")
        .matches(
          /^(9[6-8][0-9]{8}|0[1-9][0-9]{5,7})$/,
          "Enter a valid mobile or landline number"
        ),
    }),

    onSubmit: async (values, { resetForm, setSubmitting }) => {
      setError(null);
      setLoading(true); // 👈 Start loading

      const formData = new FormData();
      formData.append("OrganizationName", values.organizationName);
      formData.append("HostName", values.hostName);
      formData.append("Server", values.server);
      formData.append("DbName", values.dbName);
      formData.append("Password", values.password || "");
      formData.append("DbUser", values.dbUser);
      formData.append("Email", values.email);
      formData.append("Tel", values.tel);
      formData.append("Address1", values.address1);
      formData.append("DbPassword", values.dbPassword);
      formData.append("panNumber", values.panNumber);

      try {
        if (values.id) {
          await OrganizationService.updateOrgDatabase({
            data: formData,
            id: values.id,
          });
        } else {
          await OrganizationService.create({ data: formData });
          fetchOrganizations();
          resetForm();
        }
        toggle();
      } catch (error) {
        setError(
          error.response?.data?.title ||
            "Failed to submit form. Please try again."
        );
        console.error("Error submitting form:", error.response?.data || error);
      } finally {
        setLoading(false);
      }
    },
  });
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
    }
  }, [isOpen]);
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Org Tenant Form</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <div className="">
            <h5 className="card-title mb-4">Organization Detail</h5>
            {/* {error && <Alert color="danger">{error}</Alert>} */}
            <Row className="g-4">
              <Col md={6}>
                <div className="form-floating">
                  <Input
                    name="organizationName"
                    className="form-control"
                    placeholder="Enter organization name"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.organizationName}
                    invalid={
                      !!(
                        formik.touched.organizationName &&
                        formik.errors.organizationName
                      )
                    }
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="organizationName" className="form-label">
                    Organization Name
                  </Label>
                  {formik.touched.organizationName &&
                    formik.errors.organizationName && (
                      <FormFeedback>
                        {formik.errors.organizationName}
                      </FormFeedback>
                    )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating">
                  <Input
                    name="email"
                    className="form-control"
                    placeholder="Enter email address"
                    type="email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    invalid={!!(formik.touched.email && formik.errors.email)}
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="email" className="form-label">
                    Email Address
                  </Label>
                  {formik.touched.email && formik.errors.email && (
                    <FormFeedback>{formik.errors.email}</FormFeedback>
                  )}
                </div>
              </Col>
              {!id && (
                <Col lg={6} md={6} xs={12}>
                  <div className="form-floating position-relative">
                    <Input
                      name="password"
                      value={formik.values.password || ""}
                      type={passwordShow ? "text" : "password"}
                      className="form-control pe-5"
                      placeholder="Enter Password"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        !!(formik.touched.password && formik.errors.password)
                      }
                      style={{ padding: "0.5rem", height: "1rem" }}
                    />
                    <Label for="password" className="form-label">
                      Password
                    </Label>
                    {formik.touched.password && formik.errors.password && (
                      <FormFeedback>{formik.errors.password}</FormFeedback>
                    )}
                    <button
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-muted"
                      type="button"
                      onClick={() => setPasswordShow(!passwordShow)}
                    >
                      {passwordShow ? (
                        <i className="bx bxs-show fs-4"></i>
                      ) : (
                        <i className="bx bxs-hide fs-4"></i>
                      )}
                    </button>
                  </div>
                </Col>
              )}

              <Col md={6}>
                <div className="form-floating">
                  <Input
                    name="tel"
                    className="form-control"
                    placeholder="Enter telephone number"
                    type="number"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.tel}
                    invalid={!!(formik.touched.tel && formik.errors.tel)}
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="tel" className="form-label">
                    Telephone Number
                  </Label>
                  {formik.touched.tel && formik.errors.tel && (
                    <FormFeedback>{formik.errors.tel}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating">
                  <Input
                    name="address1"
                    className="form-control"
                    placeholder="Enter address"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.address1}
                    invalid={
                      !!(formik.touched.address1 && formik.errors.address1)
                    }
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="address1" className="form-label">
                    Address
                  </Label>
                  {formik.touched.address1 && formik.errors.address1 && (
                    <FormFeedback>{formik.errors.address1}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating">
                  <Input
                    name="panNumber"
                    className="form-control"
                    placeholder="Enter pan number"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.panNumber}
                    invalid={
                      !!(formik.touched.panNumber && formik.errors.panNumber)
                    }
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="panNumber" className="form-label">
                    Pan Number
                  </Label>
                  {formik.touched.panNumber && formik.errors.panNumber && (
                    <FormFeedback>{formik.errors.panNumber}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
            <hr className="my-4" />
            <h5 className="card-title mb-4">Database Info</h5>
            <Row className="g-4">
              <Col md={6}>
                <div className="form-floating">
                  <Input
                    name="dbName"
                    className="form-control"
                    placeholder="Enter database name"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.dbName}
                    invalid={!!(formik.touched.dbName && formik.errors.dbName)}
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="dbName" className="form-label">
                    Database Name
                  </Label>
                  {formik.touched.dbName && formik.errors.dbName && (
                    <FormFeedback>{formik.errors.dbName}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating">
                  <Input
                    name="dbUser"
                    className="form-control"
                    placeholder="Enter database user"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.dbUser}
                    invalid={!!(formik.touched.dbUser && formik.errors.dbUser)}
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="dbUser" className="form-label">
                    Database User
                  </Label>
                  {formik.touched.dbUser && formik.errors.dbUser && (
                    <FormFeedback>{formik.errors.dbUser}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col lg={6} md={6} xs={12}>
                <div className="form-floating position-relative">
                  <Input
                    name="dbPassword"
                    value={formik.values.dbPassword || ""}
                    type={dbPasswordShow ? "text" : "password"}
                    className="form-control pe-5"
                    placeholder="Enter Password"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      !!(formik.touched.dbPassword && formik.errors.dbPassword)
                    }
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="dbPassword" className="form-label">
                    Database Password
                  </Label>
                  {formik.touched.dbPassword && formik.errors.dbPassword && (
                    <FormFeedback>{formik.errors.dbPassword}</FormFeedback>
                  )}
                  <button
                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-muted"
                    type="button"
                    onClick={() => setDbPasswordShow(!dbPasswordShow)}
                  >
                    {dbPasswordShow ? (
                      <i className="bx bxs-show fs-4"></i>
                    ) : (
                      <i className="bx bxs-hide fs-4"></i>
                    )}
                  </button>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating">
                  <Input
                    name="hostName"
                    className="form-control"
                    placeholder="Enter host name"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.hostName}
                    invalid={
                      !!(formik.touched.hostName && formik.errors.hostName)
                    }
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="hostName" className="form-label">
                    Host Name
                  </Label>
                  {formik.touched.hostName && formik.errors.hostName && (
                    <FormFeedback>{formik.errors.hostName}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-floating">
                  <Input
                    name="server"
                    className="form-control"
                    placeholder="Enter server"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.server}
                    invalid={!!(formik.touched.server && formik.errors.server)}
                    style={{ padding: "0.5rem", height: "1rem" }}
                  />
                  <Label for="server" className="form-label">
                    Server
                  </Label>
                  {formik.touched.server && formik.errors.server && (
                    <FormFeedback>{formik.errors.server}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={toggle} type="button">
            Cancel
          </Button>
          <Button type="submit" color="success" disabled={loading}>
            {loading ? (
              <Spinner size="sm" />
            ) : selectedItem?.id ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default TenantForm;
